import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import { DismissCircle24Regular, ChevronDown24Regular } from "@fluentui/react-icons";
import {
  fetchRecommendations,
  rejectRecommendation,
  type Proposal,
  type RejectionReason
} from "./api";

const REASONS: RejectionReason[] = [
  "Strict Corporate Policy",
  "Too Costly",
  "Technical Constraint",
  "Already Planned"
];

const CATEGORIES = ["Security", "Cost", "Performance", "Governance"] as const;
type Category = (typeof CATEGORIES)[number];

const CATEGORY_COLOR: Record<Category, "danger" | "warning" | "success" | "informative"> = {
  Security: "danger",
  Cost: "warning",
  Performance: "success",
  Governance: "informative"
};

const useStyles = makeStyles({
  page: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXL,
    maxWidth: "960px",
    margin: "0 auto"
  },
  card: { padding: tokens.spacingHorizontalL },
  cardActions: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM
  }
});

// In a real workload these come from the Fabric item context.
const WORKSPACE_ID = "00000000-0000-0000-0000-000000000000";
const LAKEHOUSE_ID = "00000000-0000-0000-0000-000000000000";

export function App() {
  const styles = useStyles();
  const [items, setItems] = useState<Proposal[]>([]);
  const [mode, setMode] = useState<"admin" | "user" | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeCategories, setActiveCategories] = useState<Set<Category>>(
    new Set(CATEGORIES)
  );

  function toggleCategory(c: Category) {
    setActiveCategories(prev => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  }

  const visibleItems = items.filter(i =>
    activeCategories.has((i.category as Category) ?? "Governance")
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchRecommendations();
        setMode(r.mode);
        setItems(r.proposals);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function onReject(rec: Proposal, reason: RejectionReason) {
    setBusy(rec.featureId);
    setError(null);
    try {
      await rejectRecommendation({
        featureId: rec.featureId,
        reason,
        workspaceId: WORKSPACE_ID,
        lakehouseId: LAKEHOUSE_ID
      });
      setItems(prev => prev.filter(r => r.featureId !== rec.featureId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={styles.page}>
      <Text as="h1" size={800} weight="semibold">
        Adoption Agent{" "}
        {mode && (
          <Badge appearance="tint" color={mode === "admin" ? "important" : "informative"}>
            {mode === "admin" ? "tenant view" : "workspace view"}
          </Badge>
        )}
      </Text>
      <Text>
        {mode === "admin"
          ? "You're a Fabric Administrator — proposals below are derived from your tenant settings."
          : "Proposals below are derived from the workspaces you can see. Reject anything that doesn't fit — the agent remembers."}
      </Text>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {loading && <Spinner label="Scanning your environment..." />}

      {!loading && items.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {CATEGORIES.map(c => {
            const count = items.filter(i => i.category === c).length;
            const on = activeCategories.has(c);
            return (
              <Badge
                key={c}
                appearance={on ? "filled" : "outline"}
                color={CATEGORY_COLOR[c]}
                onClick={() => toggleCategory(c)}
                style={{ cursor: "pointer" }}
              >
                {c} ({count})
              </Badge>
            );
          })}
        </div>
      )}

      {visibleItems.map(rec => (
        <Card key={rec.featureId} className={styles.card}>
          <CardHeader
            header={
              <Text weight="semibold">
                <Badge
                  appearance="tint"
                  color={CATEGORY_COLOR[(rec.category as Category) ?? "Governance"]}
                  style={{ marginRight: 8 }}
                >
                  {rec.category}
                </Badge>
                {rec.title}
              </Text>
            }
            description={`${rec.summary}${rec.evidence ? "  \u00B7  " + rec.evidence : ""}${rec.workspaceName && !rec.evidence?.includes(rec.workspaceName) ? "  \u00B7  " + rec.workspaceName : ""}`}
          />
          <div className={styles.cardActions}>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="secondary"
                  icon={<DismissCircle24Regular />}
                  iconPosition="before"
                  disabled={busy === rec.featureId}
                >
                  {busy === rec.featureId ? <Spinner size="tiny" /> : "Reject"}
                  <ChevronDown24Regular />
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {REASONS.map(r => (
                    <MenuItem key={r} onClick={() => onReject(rec, r)}>
                      {r}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        </Card>
      ))}

      {!loading && items.length === 0 && !error && (
        <MessageBar intent="info">
          <MessageBarBody>
            <strong>No proposals returned</strong> (mode: <code>{mode ?? "unknown"}</code>, raw count: 0).<br />
            {mode === "admin"
              ? "Tenant scan ran successfully and every governed setting matches the recommended posture. Nothing to suggest."
              : mode === "user"
              ? "Workspace scan ran but found no gaps — every visible workspace already contains all key item types and every item has a description. Most likely cause: the OBO-exchanged Fabric token sees zero workspaces because admin consent for Workspace.ReadWrite.All / Item.ReadWrite.All has not been granted, so /v1/workspaces returned []. Check Entra ID → Enterprise Applications → AdoptionAgent-Workload → Permissions and confirm consent."
              : "The /api call did not return a recognised mode value. Open DevTools → Console for the raw response."}
          </MessageBarBody>
        </MessageBar>
      )}
      {!loading && items.length > 0 && visibleItems.length === 0 && !error && (
        <MessageBar intent="info">
          <MessageBarBody>
            {items.length} proposal(s) hidden by category filter.
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
